import React, { useState, useMemo, useCallback } from 'react';
import { LEGION_CARDS } from './legionData';
import { CardDisplay } from './CardDisplay';
import { SpreadMode, DrawnCard, SpreadPosition } from './types';
import { useAudio } from '../../audio/useAudio';

interface BrassVesselOracleProps {
  onSendToAnvil?: (phrase: string) => void;
}

const SPREAD_CONFIGS: Record<SpreadMode, { name: string; count: number; positions: SpreadPosition[] }> = {
  single: {
    name: 'Single Hammer Strike (1 Card)',
    count: 1,
    positions: [
      { index: 0, label: 'The Hammer Strike', description: 'Where the blow must fall today; unambiguous grounding verdict.' }
    ]
  },
  three: {
    name: 'Three Pillars of Earth (3 Cards)',
    count: 3,
    positions: [
      { index: 0, label: 'The Yoke', description: 'The unseen compromise, hidden bondage, or sentimental excuse.' },
      { index: 1, label: 'The Anvil', description: 'The required labor, hard confrontation, or physical crucible.' },
      { index: 2, label: 'The Sovereign Stone', description: 'The fortified reality, permanent asset, or unshakeable outcome.' }
    ]
  },
  five: {
    name: 'Juridical Cross of Belial (5 Cards)',
    count: 5,
    positions: [
      { index: 0, label: 'The Querent', description: 'Current physical and metaphysical stature upon the soil.' },
      { index: 1, label: 'The Prosecution', description: 'Hostile contracts, astral liens, and predatory claimants.' },
      { index: 2, label: 'The Defense', description: 'Inherent leverage, physical weapons, and unbreachable rights.' },
      { index: 3, label: 'The Subterranean Root', description: 'Suppressed material truth and ancestral bedrock.' },
      { index: 4, label: 'The Sovereign Verdict', description: 'The immutable decree and immediate required action.' }
    ]
  }
};

export const BrassVesselOracle: React.FC<BrassVesselOracleProps> = ({ onSendToAnvil }) => {
  const [spreadMode, setSpreadMode] = useState<SpreadMode>('single');
  const [drawnCards, setDrawnCards] = useState<DrawnCard[]>([]);
  const [catalogSearch, setCatalogSearch] = useState<string>('');
  const [selectedCohortFilter, setSelectedCohortFilter] = useState<number>(0);
  const [showCatalog, setShowCatalog] = useState<boolean>(false);
  const { playCardDraw } = useAudio();

  // Draw or Shuffle Cards
  const handleDrawCards = useCallback(() => {
    playCardDraw();
    const config = SPREAD_CONFIGS[spreadMode];
    // Fisher-Yates shuffle copy
    const pool = [...LEGION_CARDS];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    const selected = pool.slice(0, config.count).map((card, idx) => ({
      card,
      isRevealed: false,
      position: config.positions[idx]
    }));

    setDrawnCards(selected);
  }, [spreadMode, playCardDraw]);

  // Initial draw if empty
  React.useEffect(() => {
    if (drawnCards.length === 0) {
      handleDrawCards();
    }
  }, [handleDrawCards, drawnCards.length]);

  const handleFlipCard = (index: number) => {
    playCardDraw();
    setDrawnCards(prev => prev.map((c, i) => i === index ? { ...c, isRevealed: !c.isRevealed } : c));
  };

  const handleRevealAll = () => {
    playCardDraw();
    setDrawnCards(prev => prev.map(c => ({ ...c, isRevealed: true })));
  };

  // Filter Catalog
  const filteredCatalog = useMemo(() => {
    return LEGION_CARDS.filter(card => {
      const matchesSearch = catalogSearch === '' || 
        card.name.toLowerCase().includes(catalogSearch.toLowerCase()) ||
        card.counsel.verdict.toLowerCase().includes(catalogSearch.toLowerCase()) ||
        card.cohortName.toLowerCase().includes(catalogSearch.toLowerCase());
      const matchesCohort = selectedCohortFilter === 0 || card.cohortId === selectedCohortFilter;
      return matchesSearch && matchesCohort;
    });
  }, [catalogSearch, selectedCohortFilter]);

  return (
    <div className="space-y-8 animate-fade-in" id="brass-vessel-oracle-container">
      {/* Header */}
      <div className="border-b border-iron-dark pb-6">
        <div className="inline-flex items-center gap-2 rounded border border-bronze-dark/80 bg-obsidian-deep/80 px-3 py-1 text-[11px] font-display tracking-widest text-gold-ancient uppercase mb-2">
          <span>VAS ORACULI</span>
          <span>•</span>
          <span>80 LEGIONES CHALCITIS</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-parchment-light uppercase mt-1">
              The Oracle of the Brass Vessel
            </h1>
            <p className="text-xs sm:text-sm text-parchment-dim mt-1 max-w-2xl font-serif">
              Eighty subterranean legions sealed by Solomon in the bronze urn, now unbound to deliver brutal, actionable, grounding counsel.
            </p>
          </div>
          <button
            onClick={handleDrawCards}
            aria-label="Draw New Spread"
            className="btn-gold px-5 py-2.5 text-xs font-semibold rounded flex items-center gap-2 self-start sm:self-auto shadow-glow-bronze"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M7 7h10M7 12h10M7 17h10" />
            </svg>
            Unseal Vessel & Draw
          </button>
        </div>
      </div>

      {/* Controls Bar: Spread Selector & Action Buttons */}
      <div className="chthonic-card rounded-lg p-5 sm:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        {/* Spread Mode Radios */}
        <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Oracle Spread Type">
          <span className="text-[10px] font-mono text-iron-steel uppercase mr-2">SPREAD:</span>
          {(['single', 'three', 'five'] as SpreadMode[]).map((mode) => (
            <button
              key={mode}
              role="radio"
              aria-checked={spreadMode === mode}
              onClick={() => {
                setSpreadMode(mode);
              }}
              className={`px-3 py-1.5 rounded text-xs font-display tracking-wider uppercase transition-colors ${
                spreadMode === mode
                  ? 'bg-gold-bright text-obsidian-deep font-bold shadow-glow-gold'
                  : 'border border-iron-dark bg-obsidian-deep text-parchment-dim hover:text-parchment-light'
              }`}
            >
              {SPREAD_CONFIGS[mode].name.split(' (')[0]}
            </button>
          ))}
        </div>

        {/* Global Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleRevealAll}
            className="rounded border border-iron-base bg-obsidian-surface px-3 py-1.5 text-xs font-mono text-gold-ancient hover:border-gold-bright hover:text-gold-bright transition-colors"
          >
            Reveal All Cards
          </button>
          <button
            onClick={() => setShowCatalog(!showCatalog)}
            className="rounded border border-iron-base bg-obsidian-surface px-3 py-1.5 text-xs font-mono text-parchment-dim hover:text-parchment-light transition-colors"
          >
            {showCatalog ? 'Close Grimoire' : 'Browse 80 Legions'}
          </button>
        </div>
      </div>

      {/* Active Spread Card Grid */}
      <div className="space-y-4">
        <div className="text-center">
          <span className="font-display text-sm text-gold-ancient uppercase tracking-widest">
            {SPREAD_CONFIGS[spreadMode].name}
          </span>
        </div>

        <div className={`grid gap-6 ${
          spreadMode === 'single'
            ? 'grid-cols-1 max-w-sm mx-auto'
            : spreadMode === 'three'
            ? 'grid-cols-1 md:grid-cols-3'
            : 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'
        }`}>
          {drawnCards.map((drawn, idx) => (
            <CardDisplay
              key={`${drawn.card.id}-${idx}`}
              drawnCard={drawn}
              onFlip={() => handleFlipCard(idx)}
              onSendToAnvil={onSendToAnvil}
            />
          ))}
        </div>
      </div>

      {/* 80-Legion Grimoire Catalog Drawer */}
      {showCatalog && (
        <div className="chthonic-card rounded-lg p-6 sm:p-8 space-y-6 animate-fade-in border-gold-ancient">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-iron-dark pb-4">
            <div>
              <span className="text-[10px] font-mono text-gold-bright uppercase tracking-widest block">
                COMPENDIUM OCTOGINTA LEGIONUM
              </span>
              <h3 className="font-display text-xl text-parchment-light mt-1">
                The 80 Legions Catalog & Bastion Directory
              </h3>
            </div>
            <button
              onClick={() => setShowCatalog(false)}
              className="text-xs font-mono text-iron-steel hover:text-parchment-light self-start sm:self-auto"
            >
              CLOSE CATALOG [×]
            </button>
          </div>

          {/* Search & Cohort Filter */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Search by Legion name, counsel, or verdict..."
              value={catalogSearch}
              onChange={(e) => setCatalogSearch(e.target.value)}
              className="rounded border border-iron-base bg-obsidian-deep px-3 py-2 text-xs font-mono text-parchment-light placeholder-iron-muted focus:border-gold-bright focus:outline-none"
            />
            <select
              value={selectedCohortFilter}
              onChange={(e) => setSelectedCohortFilter(Number(e.target.value))}
              aria-label="Filter by Bastion Cohort"
              className="rounded border border-iron-base bg-obsidian-deep px-3 py-2 text-xs font-mono text-parchment-light focus:border-gold-bright focus:outline-none"
            >
              <option value={0}>All 8 Bastions (All 80 Legions)</option>
              <option value={1}>Cohort I: Bastion of the Anvil & Bedrock</option>
              <option value={2}>Cohort II: Bastion of Juridical Dominance</option>
              <option value={3}>Cohort III: Bastion of Subterranean Vaults</option>
              <option value={4}>Cohort IV: Bastion of the Severed Yoke</option>
              <option value={5}>Cohort V: Bastion of the Iron Tower</option>
              <option value={6}>Cohort VI: Bastion of Chthonic Necromancy</option>
              <option value={7}>Cohort VII: Bastion of Carnal Sovereign Power</option>
              <option value={8}>Cohort VIII: Bastion of the Black Crucible</option>
            </select>
          </div>

          {/* Catalog Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto pr-2">
            {filteredCatalog.map((card) => (
              <div
                key={card.id}
                className="rounded border border-iron-dark bg-obsidian-deep p-4 space-y-2 hover:border-gold-ancient transition-colors text-xs"
              >
                <div className="flex justify-between items-center text-[10px] font-mono text-gold-ancient">
                  <span>#{card.id.toString().padStart(2, '0')}</span>
                  <span>{card.rank}</span>
                </div>
                <h4 className="font-display text-sm font-bold text-parchment-light truncate">
                  {card.name}
                </h4>
                <div className="text-[10px] text-iron-steel font-serif italic truncate">
                  {card.cohortName}
                </div>
                <p className="text-[11px] font-serif text-gold-bright/90 italic leading-snug line-clamp-2">
                  "{card.counsel.verdict}"
                </p>
                <div className="pt-2 border-t border-iron-dark/60 text-[10px] text-parchment-dim">
                  <strong>Action:</strong> {card.counsel.action}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrassVesselOracle;
